/**
 * ReturnBookToWriterDialog.js — proofreader/admin/handler sends a book back to the writer.
 *
 *   • Reusable dropdown of saved reasons that learns: typing a brand-new one and
 *     confirming auto-persists it for next time.
 *   • Reasons come from GET /api/proofreading/return-reasons (5 defaults seeded
 *     on first call, including the "Herlaai in PDF/DOCX/EPUB" preset).
 *   • POST /api/proofreading/return-to-writer/{bookId} moves the book back to
 *     'Wag vir Regmaak' status and notifies the writer.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send, Plus, X } from 'lucide-react';

const NEW_REASON_KEY = '__add_new__';

export default function ReturnBookToWriterDialog({ open, book, onClose, onSent }) {
  const [reasons, setReasons] = useState([]);
  const [selected, setSelected] = useState('');
  const [newReason, setNewReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected('');
    setNewReason('');
    fetchReasons();
  }, [open]);

  const fetchReasons = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/proofreading/return-reasons`);
      setReasons(r.data || []);
    } catch (e) {
      toast.error('Kon nie redes laai nie.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    let reason = selected;
    if (selected === NEW_REASON_KEY) {
      reason = (newReason || '').trim();
      if (!reason) { toast.error('Tik asseblief ’n rede.'); return; }
    }
    if (!reason) { toast.error('Kies asseblief ’n rede.'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/proofreading/return-to-writer/${book.id}`, { reason });
      toast.success(`"${book.boek_naam}" is terug na die skrywer gestuur.`);
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie boek terugstuur nie.');
    } finally {
      setSending(false);
    }
  };

  if (!book) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="return-to-writer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Send className="h-5 w-5" /> Stuur Terug na Skrywer
          </DialogTitle>
          <DialogDescription>
            Stuur <strong>&ldquo;{book.boek_naam}&rdquo;</strong> terug na <strong>{book.skrywer_naam || 'die skrywer'}</strong>. Hulle sal die boek onder &ldquo;Wag vir Regmaak&rdquo; sien en kan ’n nuwe lêer oplaai.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="return-reason-select">Rede vir terugstuur</Label>
            <select
              id="return-reason-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white"
              disabled={loading || sending}
              data-testid="return-reason-select"
            >
              <option value="">— Kies ’n rede —</option>
              {reasons.map((r, i) => (
                <option key={i} value={r}>{r.length > 80 ? r.slice(0, 80) + '…' : r}</option>
              ))}
              <option value={NEW_REASON_KEY}>+ Voeg nuwe rede by</option>
            </select>
          </div>

          {selected === NEW_REASON_KEY && (
            <div data-testid="return-new-reason-block">
              <Label htmlFor="return-new-reason">Nuwe rede</Label>
              <Textarea
                id="return-new-reason"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="Bv. Lêer is nie ’n PDF, DOCX of EPUB nie — laai asseblief in een van hierdie formate op."
                rows={3}
                maxLength={500}
                className="mt-1"
                disabled={sending}
                data-testid="return-new-reason-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hierdie rede word outomaties bygevoeg by die afrol-lys vir volgende keer.
              </p>
            </div>
          )}

          {selected && selected !== NEW_REASON_KEY && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-gray-700" data-testid="return-reason-preview">
              <strong>Voorskou:</strong> {selected}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose && onClose()} disabled={sending} data-testid="return-cancel-btn">
            <X className="h-4 w-4 mr-1" /> Kanselleer
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !selected || (selected === NEW_REASON_KEY && !newReason.trim())}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="return-confirm-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Stuur Terug
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export the icon for callers that want the trigger button to look consistent
export { Plus as ReturnBookIcon };
